import React from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import PrintableAssetLabel from '../../../components/PrintableAssetLabel';

const QRCodeModal = ({ asset, onClose }) => {
  const { t } = useTranslation();
  if (!asset) return null;

  const qrCodeUrl = `${window.location.origin}/assets/${asset.asset_tag}`;

  const handlePrint = () => {
    const qrElement = document.getElementById("QRCodeSvg");
    
    if (!qrElement) {
      console.error("QR Element not found in DOM");
      window.print();
      return;
    }

    let qrContent = '';
    if (qrElement.tagName.toLowerCase() === 'canvas') {
      const dataUrl = qrElement.toDataURL('image/png');
      qrContent = `<img src="${dataUrl}" style="width: 100%; height: auto;" />`;
    } else {
      qrContent = qrElement.outerHTML;
    }

    const printWindow = window.open('', '_blank', 'width=800,height=800');
    if (!printWindow) {
      alert(t('asset_details.allow_popups'));
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>${t('asset_details.print_asset_label_title', { tag: asset.asset_tag })}</title>
          <style>
            body { 
              margin: 0; 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              height: 100vh;
              background-color: white;
            }
            .sticker {
              width: 50mm;
              height: 25mm;
              border: 1px solid black;
              padding: 2mm;
              display: flex;
              flex-direction: column;
              font-family: Arial, sans-serif;
              box-sizing: border-box;
            }
            .header { font-size: 10px; font-weight: bold; margin-bottom: 4px; text-align: center; }
            .content { display: flex; flex-direction: row; align-items: center; justify-content: space-between; gap: 8px; }
            .qr-container { width: 45px; height: 45px; flex-shrink: 0; }
            .info { flex: 1; text-align: right; overflow: hidden; }
            .label { font-size: 7px; color: #666; text-transform: uppercase; font-weight: bold; display: block; line-height: 1; }
            .value { font-size: 11px; font-weight: 900; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; margin-bottom: 2px; }
            .sn-value { font-size: 9px; font-weight: 500; }
            .footer { margin-top: auto; border-top: 0.5px solid #eee; pt: 2px; }
            .footer-text { font-size: 6px; color: #999; text-align: center; margin: 2px 0 0 0; }
            svg { width: 100% !important; height: auto !important; }
          </style>
        </head>
        <body>
          <div class="sticker">
            <div class="header">PANASONIC ISD</div>
            <div class="content">
              <div class="qr-container">${qrContent}</div>
              <div class="info">
                <span class="label">${t('labels.asset_tag')}</span>
                <span class="value">${asset.asset_tag}</span>
                <span class="label">${t('labels.serial_number')}</span>
                <span class="value sn-value">${asset.serial_number || t('common.na')}</span>
              </div>
            </div>
            <div class="footer">
              <p class="footer-text">${t('asset_details.asset_management_system')}</p>
            </div>
          </div>
          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 300);
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };
  
  const handleDownload = () => {
    const svg = document.getElementById("QRCodeSvg");
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `${asset.asset_tag}_qrcode.png`;
      downloadLink.href = `${pngFile}`;
      downloadLink.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  return (
    <>
      {/* Sticker layout for physical printing - only visible during print */}
      <PrintableAssetLabel asset={asset} />
      
      {/* The modal container - hidden during print */}
      <div className="print:hidden fixed inset-0 bg-black/50 z-300 flex items-center justify-center p-4 overflow-y-auto">
        
        {/* The actual modal content panel */}
        <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md shadow-2xl">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">{t('asset_details.qr_code')}</h3>
            <Button variant="ghost" size="icon" iconName="X" onClick={onClose} />
          </div>

          {/* QR Code and Info */}
          <div className="text-center">
            <div className="bg-card p-6 inline-block rounded-xl border border-border shadow-sm mb-6">
              <QRCode value={qrCodeUrl} size={200} id="QRCodeSvg" level="H" />
            </div>
            <div className="space-y-3">
              <div className="text-2xl font-bold text-foreground">{asset?.product_name}</div>
              <div className="inline-flex items-center px-3 py-1 bg-primary/10 text-primary rounded-full font-mono font-bold">
                {asset?.asset_tag}
              </div>
              <p className="text-sm text-muted-foreground mt-4 break-all font-mono opacity-60">
                {qrCodeUrl}
              </p>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex gap-3 mt-8">
            <Button variant="outline" iconName="Printer" onClick={handlePrint} className="flex-1">
              {t('asset_details.print_label')}
            </Button>
            <Button variant="default" iconName="Download" onClick={handleDownload} className="flex-1">
              {t('asset_details.download_png')}
            </Button>
          </div>

        </div>
      </div>
    </>
  );
};

export default QRCodeModal;
